import React, { useState, useEffect, useContext } from "react";
import { Box, Button, FormControl, Grid, IconButton, Stack, Typography } from "@mui/material";
import { useHistory } from "react-router-dom";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import SimpleModal from "common/components/dialogs/SimpleModal";
import SelectImageFileDialog from "./SelectImageFileDialog";
import GuestUser from "common/components/dialogs/GuestUser";
import useProjectStore, { INewProject } from "common/store/ProjectStore";
import { useAuthContext } from "common/contexts/AuthContext";
import useSettingsStore from "common/store/SettingsStore";
import useNotificationStore from "common/store/NotificationStore";
import IReturnResults from "common/interfaces/IReturnResults";
import { LanguageContext } from "common/contexts/LanguageContext";
import DialogTextField from "common/components/dialogs/DialogTextField";
import useUserStore from "common/store/UserStore";
import { ValidInputCharsRegEx } from "common/constants/GlobalValues";
import LoadingSpinnerBackdrop from "common/components/progress/LoadingSpinner";
import theme from "common/theme/theme";
import { useAuth0 } from '@auth0/auth0-react';

interface IPlotImages {
  id: number;
  altName: string;
  image: string;
}

interface CreateProjectDialogProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
const CreateProjectDialog: React.FC<CreateProjectDialogProps> = ({ isOpen, setOpen }) => {
  const [signalSelectImageOpen, setSignalSelectImageOpen] = useState<number>(0);
  const [selectedPlotId, setSelectedPlotId] = useState<number>(-1);
  const [projectName, setProjectName] = useState<string>("");
  const [customImage, setCustomImage] = useState<string | null>(null);
  const [openGuestUserPopUp, setOpenGuestUserPopUp] = useState<boolean>(false);
  const [createClicked, setCreateClicked] = useState(false);
  const [timeoutId, setTimeoutId] = useState<ReturnType<typeof setTimeout>>();

  const { createProject, setProjectID, setName, setPlotPlanImage, addNetworkContainer, resetProject, loading } = useProjectStore();
  const settings = useSettingsStore((state) => state.settings);
  const { enqueueNotification } = useNotificationStore();
  const { projects } = useUserStore();
  const { selectedLanguageLocale, getString } = useContext(LanguageContext);
    const { isAuthenticatedUser, getAccessToken } = useAuthContext();

  const history = useHistory();

  useEffect(() => {
    if (isOpen === false) {
      setProjectName("");
      setSelectedPlotId(-1);
      setCustomImage(null);
    }
  }, [isOpen]);

  const plotImages: IPlotImages[] = [
    {
      id: 0,
      altName: "Custom",
      image: "",
    },
    {
      id: 1,
      altName: "Plot 1",
      image: "/assets/stock-plotplans/1-resized.jpg",
    },
    {
      id: 2,
      altName: "Plot 2",
      image: "/assets/stock-plotplans/2-resized.jpg",
    },
    {
      id: 3,
      altName: "Plot 3",
      image: "/assets/stock-plotplans/3-resized.jpg",
    },
    {
      id: 4,
      altName: "Plot 4",
      image: "/assets/stock-plotplans/4-resized.jpg",
    },
  ];

  const handleNewImageButtonClick = () => {
      if (!isAuthenticatedUser) {
      setOpenGuestUserPopUp(true);
    } else {
      setSignalSelectImageOpen(signalSelectImageOpen + 1);
    }
  };

  const handleCustomImageSelected = (image: string) => {
    setCustomImage(image);
    setSelectedPlotId(0);
  };

  const fetchBase64Image = async (url: string) => {
    const response = await fetch(url);
    const blob = await response.blob();
    const reader = new FileReader();
    await new Promise((resolve, reject) => {
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    return { blobType: blob.type, base64data: reader.result };
  };

  const handleAddProjectButtonClick = async () => {
    try {
      if (createClicked) {
        return;
      }
      setCreateClicked(true);
      clearTimeout(timeoutId);
      const id = setTimeout(() => {
        setCreateClicked(false);
      }, 3000);
      setTimeoutId(id);
      if (!ValidInputCharsRegEx.test(projectName)) {
        enqueueNotification("invalid_chars", `${getString('msg_invalid_chars')} ${getString('ProjectName')}`, "error");
        return;
      }
  
      let response: IReturnResults | null = null;
      setProjectID(null);
        if (selectedPlotId === 0 && isAuthenticatedUser) {
        if(checkDuplicateName(projectName)) {
          throw new Error(getString('dlg_crt_proj_error_name'));
        }
        if (customImage !== null) {
          let project: INewProject = {
            projectName: projectName,
            projectImage: "",
            contentType: "",
          };
          const imageData = await fetchBase64Image(customImage);
          if (imageData.base64data !== null && typeof imageData.base64data === "string") {
            project.projectImage = imageData.base64data.split(",")[1];
            project.contentType = imageData.blobType;
            response = await createProject(settings.apiBaseUrl, getAccessToken, project);
          } else {
            throw new Error(getString('dlg_crt_proj_error_img_1'));
          }
        } else {
          throw new Error(getString('dlg_crt_proj_error_img_2'));
        }
        } else if (isAuthenticatedUser) {
        if(checkDuplicateName(projectName)) {
          throw new Error(getString('dlg_crt_proj_error_name'));
        }
        let project: INewProject = {
          projectName: projectName,
          projectImage: "",
          contentType: selectedPlotId.toString(),
        };
        response = await createProject(settings.apiBaseUrl, getAccessToken, project);
      } else {
        //not logged in will not call createProject
        resetProject();
        setProjectID(-1);
        setName(projectName);
        setPlotPlanImage("", selectedPlotId.toString());
        //add Default Network
        addNetworkContainer("Default Network");
      }

      if (response !== null) {
        if (response.succeeded) {
          enqueueNotification("create_project_success", getString('dlg_crt_proj_success'), "success");
        } else {
          throw new Error(response.errorMessage!);
        }
      }
      setOpen(false);
      history.push(`/${selectedLanguageLocale}/project/${useProjectStore.getState().projectID}`);
    } catch (err) {
      enqueueNotification("create_project_failure", getString('dlg_crt_proj_error') + " " + err, "error");
    }
  };

  const checkDuplicateName = (name: string) => {
    //true if duplicate, else false
    return projects.some(proj => proj.projectName === name);
  };

  return (
    <SimpleModal title={getString("dlg_crt_proj_title")} maxWidth="md" isOpen={isOpen} setOpen={setOpen}>
      <FormControl sx={{ width: "55%", mt: "16px", mb: "24px" }}>
        <DialogTextField
          label={getString("dlg_crt_proj_name_lbl")}
          size="small"
          variant="filled"
          onChange={(event) => setProjectName(event.target.value)}
          inputProps={{
            maxLength: 250,
          }}
        />
      </FormControl>
      <Typography sx={{ textTransform: "uppercase", color: "black", fontSize: "0.75rem", fontWeight: "600" }}>
        {getString("dlg_crt_proj_plot_lbl")}
      </Typography>

      <Box sx={{ marginLeft: "auton", display: "flex", width: "100%" }}>
        <Grid container spacing={1}>
          {customImage === null && (
            <Grid item>
              <Button
                variant="contained"
                sx={{
                  width: "144px",
                  height: "96px",
                  marginRight: "12px",
                  mt: "6px",
                  pl: "6px",
                  pr: "6px",
                    backgroundColor: isAuthenticatedUser ? "auto" : theme.secret.gray,
                    color: isAuthenticatedUser ? "auto" : "#B2B2B2",
                  borderRadius: "0",
                }}
                onClick={handleNewImageButtonClick}
              >
                <Stack direction="column">
                  <div style={{ fontSize: ".75rem", fontWeight: "500" }}>{getString("dlg_crt_proj_own_img_1")}</div>
                  <div style={{ fontSize: ".5rem", fontWeight: "500", fontStyle: "italic" }}>
                    {getString("dlg_crt_proj_own_img_2")}
                  </div>
                </Stack>
                <Typography variant="h6"></Typography>
              </Button>
            </Grid>
          )}
          {plotImages
            .filter((item) => item.id > 0 || (item.id === 0 && customImage !== null))
            .map((plot, index) => (
              <Grid item key={index}> 
                <IconButton
                  aria-selected={selectedPlotId === plot.id}
                  onClick={() => setSelectedPlotId(plot.id)}
                  sx={{
                    borderRadius: "0",
                    bgcolor: "transparent",
                    '&[aria-selected="true"]': {
                      borderWidth:"2px",
                      borderStyle: "solid",
                      borderColor: "primary.light",
                    },
                    transition: 'transform 0.3s',
                    "&:hover": {
                      bgcolor: selectedPlotId === plot.id ? "primary.dark" : "transparent", //primary.dark",
                      transform: 'scale(1.15)'
                    },
                  }}
                >
                  <img
                    style={{ width: "144px", height: "96px" }}
                    src={plot.id > 0 ? plot.image : customImage!} // Second customImage null check shouldn't be necessary, since it's filtered out above but TS is complaining
                    alt={plot.altName}
                  />
                </IconButton>
              </Grid>
            ))}
        </Grid>
      </Box>
      <Grid container direction="column" justifyContent="center" alignItems="center" spacing={2} sx={{ mt: "1px" }}>
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            disabled={selectedPlotId < 0 || projectName === ""}
            onClick={handleAddProjectButtonClick}
            sx={{
              "&.Mui-disabled": {
                background: theme.secret.gray,
              }
            }}
          >
            {getString("cmn_confirm")}
            <ChevronRightOutlinedIcon />
          </Button>
        </Grid>
        <Grid item>
          <Button variant="outlined" color="primary" onClick={() => setOpen(false)}>
            {getString("cmn_cancel")}
          </Button>
        </Grid>
      </Grid>
      <SelectImageFileDialog signalOpen={signalSelectImageOpen} setImage={handleCustomImageSelected} />
      <GuestUser title={getString("msg_guest_user_create")} isOpen={openGuestUserPopUp} setOpen={() => setOpenGuestUserPopUp(false)}></GuestUser>
      {loading && <LoadingSpinnerBackdrop />}
    </SimpleModal>
  );
};

export default CreateProjectDialog;
